import React, { useState, useEffect, useRef } from 'react';
import { Box, styled } from '@mui/material';
import { SvgLineChart, ChartLegend } from '@/components/ds/charts';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '@/urls';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatCurrencyNoUnit } from '@/lib/formatters';

const StyledChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  position: 'relative',
  width: '100%',
  minHeight: 500,
}));

interface AnnualBudgetConsumptionChartProps {
  budgetType: 'global' | 'department' | 'system';
  entityId?: number;
  refreshTrigger?: number;
  budgetCurrency?: string;
}

export default function AnnualBudgetConsumptionChart({
  budgetType,
  entityId,
  refreshTrigger,
  budgetCurrency,
}: Readonly<AnnualBudgetConsumptionChartProps>) {
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { getDisplayCurrency } = useCurrency();
  const displayCurrency = getDisplayCurrency(budgetCurrency);
  const lastRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        const params: {
          departmentId?: number | null;
          systemId?: number | null;
          currency?: string;
        } = { currency: displayCurrency };

        if (budgetType === 'department') params.departmentId = entityId ?? null;
        if (budgetType === 'system') params.systemId = entityId ?? null;

        // Skip invalid requests
        if ((budgetType === 'department' || budgetType === 'system') && !entityId) {
          setChartData(null);
          return;
        }

        const url = urls.DevOps.getAnnualBudgetConsumptionV2(params);
        const requestKey = `${budgetType}:${entityId ?? 'global'}:${displayCurrency}`;
        if (lastRequestKeyRef.current === requestKey) {
          return;
        }
        lastRequestKeyRef.current = requestKey;

        const data = await fetchWrapper.get(url);
        setChartData(data);
      } catch (error) {
        console.error('Error fetching annual consumption data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [budgetType, entityId, refreshTrigger, displayCurrency]);

  if (loading || !chartData || !Array.isArray(chartData.monthlyData)) {
    return <div style={{ color: '#8A94A6', fontSize: 13 }}>Loading chart...</div>;
  }

  const monthsLabels = chartData.monthlyData.map((item: any) => item.month);
  const actualData = chartData.monthlyData
    .map((item: any) => item.cumulative)
    .filter((v: any) => v !== null && v !== undefined);
  const forecastData = chartData.monthlyData
    .map((item: any) => item.forecast)
    .filter((v: any) => v !== null && v !== undefined);
  const budgetData = chartData.monthlyData
    .map((item: any) => item.monthlyBudget)
    .filter((v: any) => v !== null && v !== undefined);

  const annualBudget = budgetData.length ? Math.max(...budgetData) : undefined;

  return (
    <StyledChartContainer>
      <SvgLineChart
        actual={actualData}
        forecast={forecastData}
        forecastOverlap={false}
        budgetLine={annualBudget}
        totalSlots={Math.max(1, monthsLabels.length - 1)}
        xLabels={monthsLabels}
        pointLabels={monthsLabels}
        fmt={formatCurrencyNoUnit}
        budgetLabel="Annual Budget"
        h={450}
        seriesLabels={{
          actual: 'Spending',
          forecast: 'Forecast',
          budget: 'Annual Budget',
        }}
      />
      <Box sx={{ mt: 1.5, width: '100%', maxWidth: 320 }}>
        <ChartLegend
          items={[
            { color: '#1C71E3', name: 'Spending' },
            { color: '#F28E2C', name: 'Forecast' },
            { color: '#E23D3D', name: 'Annual Budget' },
          ]}
        />
      </Box>
    </StyledChartContainer>
  );
}
